import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { useCurrentUser } from '../auth/useCurrentUser';
import { firstGrantedSurfacePath } from '../auth/permissionCatalog';
import { tenantApi } from '../api/tenantApi';
import { paths } from './paths';

/**
 * Side-effect component (renders nothing) that sends `hide_chrome` holders
 * straight to their first granted surface on the picker routes (decisions
 * §11, design §6.3) — they never see the tenant/customer/sponsor picker.
 * That surface is Insights when the holder is paired with an insights
 * permission (the seeded case), but `firstGrantedSurfacePath` falls through
 * to their real next surface — or `/access-denied` for a zero-surface role —
 * rather than pinning unconditionally, so a misconfigured hide_chrome-
 * without-insights role never loops into a page that denies it (adversarial
 * review P1). Mounted inside `AuthedShell` next to `AppAccessRedirect`, so
 * it runs for every logged-in user but only ACTS for the narrow bare-chrome
 * case.
 *
 * Tenant list comes from `tenantApi.getAll()` (server-filtered by grants/
 * groups when ACCESS_ENFORCEMENT is on), sorted by `tenant_code` for a
 * deterministic pick. Zero accessible tenants → `/access-denied` with a
 * dedicated "no tenant access" reason (AccessDenied reads it from
 * `location.state`, since it's unrelated to the `/api/me` 403 reasons the
 * page otherwise handles).
 */
const LandingRedirect: React.FC = () => {
  const currentUser = useCurrentUser();
  const { rbacV2Enabled, hasPermission, isLoading } = currentUser;
  const navigate = useNavigate();
  const location = useLocation();

  const isPickerRoute = location.pathname === '/' || location.pathname.startsWith('/customers/');
  const bareChrome = rbacV2Enabled && hasPermission('hide_chrome');
  const active = bareChrome && isPickerRoute && !isLoading;

  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantApi.getAll(),
    enabled: active,
  });

  useEffect(() => {
    if (!active || !tenantsQuery.isSuccess) return;
    if (tenantsQuery.data.length === 0) {
      navigate(paths.accessDenied(), { replace: true, state: { reason: 'no_tenant_access' } });
      return;
    }
    // Multi-tenant bare-chrome users land on the picker so they can choose —
    // only auto-redirect when there is exactly one accessible tenant.
    if (tenantsQuery.data.length === 1) {
      const [first] = tenantsQuery.data;
      navigate(firstGrantedSurfacePath(currentUser, first.tenant_code), { replace: true });
    }
  }, [active, tenantsQuery.isSuccess, tenantsQuery.data, navigate, currentUser]);

  return null;
};

export default LandingRedirect;
